import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui';
import { CLIENT_BUILD, fetchHealth, useUpdateCheck, type BuildInfo } from './version';

/**
 * « A new version is available » (P5g, backlog §16.7) when the Worker runs another build than this tab. Never reloads
 * by itself: an edit in progress stays until the person chooses « Reload ». « Later » hides it until a newer build.
 */
export function UpdateBanner({
  check = fetchHealth,
  client = CLIENT_BUILD,
  reload = () => window.location.reload(),
}: {
  check?: () => Promise<BuildInfo | null>;
  client?: BuildInfo;
  reload?: () => void;
}) {
  const { t } = useTranslation();
  const newer = useUpdateCheck(check, client);
  const [later, setLater] = useState<BuildInfo | null>(null);
  if (!newer || newer === later) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      data-update-banner=""
      className="os-enter fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] left-1/2 z-40 flex w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-lg border border-border bg-surface px-3 py-2 font-sans text-sm text-fg shadow-lg"
    >
      <span>{t('about.update.message')}</span>
      <span className="flex gap-1">
        <Button variant="primary" size="sm" className="pointer-coarse:h-11" onClick={reload}>
          {t('about.update.reload')}
        </Button>
        <Button variant="ghost" size="sm" className="pointer-coarse:h-11" onClick={() => setLater(newer)}>
          {t('about.update.later')}
        </Button>
      </span>
    </div>
  );
}
